/**
 * 領獎身分守衛（client only）。
 * 事故路徑、判定序列與 Non-Goals 見 spec/JT-1784889480_identity-generation-invariant.md §2 / §3 / §5.4。
 */

import { watch } from 'vue'

import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useReward } from '@/composables/rewards/useReward'
import { useRewardStore } from '@/stores/rewards/reward'
import { hasIdentityChanged, isNonEmptyString } from '@/utils/identityGuard'

export default defineNuxtPlugin(() => {
  // 所有相依在 plugin setup 階段就解析完 —— watch callback 會在離開 Nuxt context 後才執行，
  // 屆時再呼叫 use* 可能拿不到 nuxtApp
  const { token, isLoggedIn } = useAuthToken()
  const rewardStore = useRewardStore()
  const { fetchRewardList } = useReward()

  let lastNonEmptyToken = isNonEmptyString(token.value) ? token.value : null

  const stop = watch(token, (nextToken) => {
    // BR-1：對空值早退且「不更新基準」，才能同時吸收 extendToken() 的 T → null → T 瞬態
    // 並抓得到穿越 null 的 T → null → T2（規格 §5.4）
    if (!isNonEmptyString(nextToken)) {
      return
    }

    const previousToken = lastNonEmptyToken
    lastNonEmptyToken = nextToken

    if (!hasIdentityChanged(previousToken, nextToken)) {
      return
    }

    rewardStore.resetUserScoped()

    // 清完要抓得回來，否則畫面會停在空清單。
    // 背景重抓：吞掉 rejection，避免 unhandledrejection 上報 Sentry；
    // 真實錯誤已於 request 層上報，失敗等下一輪輪詢重試
    if (isLoggedIn.value) {
      fetchRewardList().catch(() => {})
    }
  })

  // client plugin 的 watch 與整個 app 同生命週期，隨頁面卸載自然回收；
  // ssr-cleanup.server.js 只跑在 server，掛 context 給它清是不可達的死碼。
  // dev HMR 重載本檔時 watch 會重複註冊，這裡才是 client 端真的會執行的清理時機
  if (import.meta.hot) {
    import.meta.hot.dispose(stop)
  }
})
